import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import { SCRIPTS_SOURCES } from '../../app/util/intune/scripts'
import { getSelectedCustomerId } from '../selectors/customers'
import prepareHeaders from '../utils/prepareHeaders'

const DEVICE_MANAGEMENT_SCRIPTS_URL = '/secure/policies/intune/deviceManagement-deviceManagementScripts'
const DEVICE_SHELL_SCRIPTS_URL = '/secure/policies/intune/deviceManagement-deviceShellScripts'
const CONFIGURATION_POLICIES_URL = '/secure/policies/intune/deviceManagement-configurationPolicies'

const LINUX_TEMPLATE_FAMILY = 'deviceConfigurationScripts'

const getScriptUrl = (source) => {
  if (source === SCRIPTS_SOURCES.CONFIGURATION_POLICIES) return CONFIGURATION_POLICIES_URL
  if (source === SCRIPTS_SOURCES.DEVICE_SHELL) return DEVICE_SHELL_SCRIPTS_URL
  return DEVICE_MANAGEMENT_SCRIPTS_URL
}

const injectDeviceManagementSource = (item) => ({ ...item, source: SCRIPTS_SOURCES.DEVICE_MANAGEMENT })
const injectDeviceShellSource = (item) => ({ ...item, source: SCRIPTS_SOURCES.DEVICE_SHELL })
const injectConfigPolicySource = (item) => ({
  ...(item.rawDataFlat ?? {}),
  id: item.id,
  displayName: item.rawDataFlat?.name ?? item.name,
  source: SCRIPTS_SOURCES.CONFIGURATION_POLICIES,
})

export const intuneScriptsApi = createApi({
  reducerPath: 'intuneScripts',
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getScriptById: builder.query({
      queryFn: async ({ scriptId, source, customerId }, _queryApi, _extraOptions, baseQuery) => {
        const isShell = source === SCRIPTS_SOURCES.DEVICE_SHELL
        const isConfigPolicy = source === SCRIPTS_SOURCES.CONFIGURATION_POLICIES

        let url
        let injectSource
        if (isConfigPolicy) {
          url = `${CONFIGURATION_POLICIES_URL}/${scriptId}`
          injectSource = injectConfigPolicySource
        } else if (isShell) {
          url = `${DEVICE_SHELL_SCRIPTS_URL}/${scriptId}`
          injectSource = injectDeviceShellSource
        } else {
          url = `${DEVICE_MANAGEMENT_SCRIPTS_URL}/${scriptId}`
          injectSource = injectDeviceManagementSource
        }

        const result = await baseQuery({ url, params: { customerId } })

        if (result.error) return { error: result.error }

        return { data: result.data ? injectSource(result.data) : result.data }
      },
    }),
    getScripts: builder.query({
      queryFn: async (customerId, _queryApi, _extraOptions, baseQuery) => {
        const [deviceManagementResult, deviceShellResult, configPoliciesResult] = await Promise.all([
          baseQuery({ url: DEVICE_MANAGEMENT_SCRIPTS_URL, params: { customerId } }),
          baseQuery({ url: DEVICE_SHELL_SCRIPTS_URL, params: { customerId } }),
          baseQuery({
            url: CONFIGURATION_POLICIES_URL,
            params: { customerId, templateFamily: LINUX_TEMPLATE_FAMILY, includeAssignments: true },
          }),
        ])

        const allFailed = deviceManagementResult.error && deviceShellResult.error && configPoliciesResult.error
        if (allFailed) {
          return { error: deviceManagementResult.error }
        }

        return {
          data: [
            ...(Array.isArray(deviceManagementResult.data)
              ? deviceManagementResult.data.map(injectDeviceManagementSource)
              : []),
            ...(Array.isArray(deviceShellResult.data) ? deviceShellResult.data.map(injectDeviceShellSource) : []),
            ...(Array.isArray(configPoliciesResult.data)
              ? configPoliciesResult.data.map(injectConfigPolicySource)
              : []),
          ],
        }
      },
    }),
    createScript: builder.mutation({
      query: ({ customerId, userId, source, hubTaskSource, payload }) => ({
        url: getScriptUrl(source),
        body: payload,
        params: { customerId, userId, source: hubTaskSource },
        method: 'POST',
      }),
    }),
    deleteScript: builder.mutation({
      query: ({ policyId, source, customerId, userId }) => ({
        url: getScriptUrl(source),
        params: { customerId, userId, policyId },
        method: 'DELETE',
      }),
    }),
  }),
})

export const {
  useGetScriptByIdQuery,
  useLazyGetScriptByIdQuery,
  useGetScriptsQuery,
  useLazyGetScriptsQuery,
  useCreateScriptMutation,
  useDeleteScriptMutation,
} = intuneScriptsApi

export const useGetScriptById = ({ scriptId, source }, options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetScriptByIdQuery({ scriptId, source, customerId }, { ...options, skip: options.skip || !scriptId })
}

export const useGetScripts = (options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetScriptsQuery(customerId, options)
}
